import React, { useState } from "react";
import {
  Button,
  Tooltip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

import { Artefact } from "@/types/artefacts";
import { useArtefactActions } from "@/hooks/useArtefactActions";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { getDownloadOptions } from "@/utils/downloadOptions";
import { DownloadFormat } from "@/utils/artefactFormatDetection";

interface ArtefactActionsProps {
  artefact: Artefact;
  onLineNumbersToggle?: (showLineNumbers: boolean) => void;
}

export const ArtefactActions: React.FC<ArtefactActionsProps> = ({
  artefact,
  onLineNumbersToggle,
}) => {
  const { t } = useTranslation();
  const { handleCopy, handleRefresh, handleDownloadWithFormat } = useArtefactActions();
  const { showLineNumbers, toggleLineNumbers, showAdvancedDownloadOptions } = useUIPreferences();

  const [isDownloading, setIsDownloading] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<DownloadFormat | null>(null);

  const hasRefreshAction = artefact.type === "html";
  const hasLineNumbersToggle = artefact.type === "code";

  const handleToggleLineNumbers = () => {
    toggleLineNumbers();
    onLineNumbersToggle?.(!showLineNumbers);
  };

  const downloadOptions = getDownloadOptions(artefact, showAdvancedDownloadOptions);

  const handleDownloadOption = async (format: DownloadFormat) => {
    setIsDownloading(true);
    setCurrentFormat(format);

    try {
      await handleDownloadWithFormat(artefact, format);
    } finally {
      setIsDownloading(false);
      setCurrentFormat(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {hasRefreshAction && (
        <Tooltip content={t("Refresh content")}>
          <Button isIconOnly size="sm" variant="flat" onPress={() => handleRefresh(artefact.id)}>
            <Icon height={16} icon="solar:refresh-bold" width={16} />
          </Button>
        </Tooltip>
      )}

      {hasLineNumbersToggle && (
        <Tooltip content={showLineNumbers ? t("Hide line numbers") : t("Show line numbers")}>
          <Button
            isIconOnly
            color={showLineNumbers ? "primary" : "default"}
            size="sm"
            variant={showLineNumbers ? "solid" : "flat"}
            onPress={handleToggleLineNumbers}
          >
            <Icon height={16} icon="solar:list-bold" width={16} />
          </Button>
        </Tooltip>
      )}

      <Tooltip content={t("Copy content")}>
        <Button isIconOnly size="sm" variant="flat" onPress={() => handleCopy(artefact)}>
          <Icon height={16} icon="solar:copy-bold" width={16} />
        </Button>
      </Tooltip>

      <div className="relative">
        <Tooltip content={t("Download")}>
          <div>
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button
                  isIconOnly
                  aria-label={t("Download options")}
                  isDisabled={isDownloading}
                  size="sm"
                  variant="flat"
                >
                  <Icon height={16} icon="solar:download-bold" width={16} />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t("Download format options")}
                disabledKeys={isDownloading ? ["all"] : []}
              >
                {downloadOptions.map((option) => (
                  <DropdownItem
                    key={option.key}
                    description={t(option.description)}
                    startContent={
                      isDownloading && currentFormat === option.format ? (
                        <Spinner size="sm" />
                      ) : (
                        <Icon height={20} icon={option.icon} width={20} />
                      )
                    }
                    onPress={() => handleDownloadOption(option.format)}
                  >
                    {t(option.label)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
